import { call, put, take, takeLatest, select } from "redux-saga/effects";
import api from "../api/api";
import PostApi from "../api/PostApi";
import {
  loadMorePostsSuccess,
  loadMorePostsFailed,
  hidePostModal,
  showAlert,
  setPostCategoryFilter,
  clearPosts,
  ratePostFailed,
  ratePostSuccess,
} from "../actions";
import { incrementUserLikes } from "../actions/userActions";
import {
  deletePostOptimistic,
  deletePostFailed,
  hidePostOptimistic,
  hidePostFailed,
  unhidePostOptimistic,
  unhidePostFailed,
  loadCurrentPostByIdRequest,
  loadCurrentPostSuccess,
  loadCurrentPostFailed,
  likePostOptimistic,
  likePostFailed,
  unlikePostOptimistic,
  unlikePostFailed,
  loadPostComments,
  loadPostCommentsSuccess,
  setCurrentPostCommentsFetchingStatus,
  loadSimilarPostsSuccess,
  loadSimilarPosts,
} from "../actions/postActions";
import { push } from "redux-little-router";
import { incrementPostViewsMP } from "../services/mixpanel";
import mixpanel from "../services/mixpanel";
import windowDataLayer from "../util/windowDataLayer";
import { shouldWaitOnAuthDone } from "../sagas/AuthSaga";

///////////////////////// Action Type Bases \\\\\\\\\\\\\\\\\\\\\\\\\
const LOAD_POSTS_ACTION_BASE = "LOAD_MORE_POSTS";
const LOAD_CURRENT_POST_BY_SLUG_ACTION_BASE = "LOAD_CURRENT_POST_BY_SLUG";
const LOAD_CURRENT_POST_BY_ID_ACTION_BASE = "LOAD_CURRENT_POST_BY_ID";
const DELETE_POST_ACTION_BASE = "DELETE_POST";
const HIDE_POST_ACTION_BASE = "HIDE_POST";
const UNHIDE_POST_ACTION_BASE = "UNHIDE_POST";
const FLOAT_POST_ACTION_BASE = "FLOAT_POST";
const LIKE_POST_ACTION_BASE = "LIKE_POST";
const UNLIKE_POST_ACTION_BASE = "UNLIKE_POST";
const LOAD_POST_COMMENTS_ACTION_BASE = "LOAD_POST_COMMENTS";
const APPLY_POST_CATEGORY_FILTER = "APPLY_POST_CATEGORY_FILTER";

///////////////////////// HANDLERS \\\\\\\\\\\\\\\\\\\\\\\\\
function* handleLoadPostsRequests({ limit, override, pagingToken, appliedFilters }) {
  const count = yield select(state => state.posts.data.length);

  let request = PostApi.getLoadPostsUrl({
    override,
    limit,
    pagingToken,
    feed_index: count,
    ...appliedFilters,
  });
  try {
    const {
      data: { data: posts, pagingToken: newPagingToken },
    } = yield call(api.get, request.url, request.config);
    yield put(loadMorePostsSuccess({ override, limit, posts, pagingToken: newPagingToken }));
    //fire ad impression urls
    Promise.all(
      posts
        .filter(post => post.sponsered)
        .map(ad => ad.impressionUrl)
        .map(api.get)
    );

    incrementPostViewsMP(posts.length);
  } catch (error) {
    yield put(loadMorePostsFailed(error));
  }
}

export function* handleLoadPostBySlugRequests({ slug }) {
  const request = PostApi.getLoadPostBySlugUrl(slug);
  yield handleLoadPost(request);
}

export function* handleLoadPostByIdRequests({ id }) {
  const request = PostApi.getLoadPostByIdUrl(id);
  yield handleLoadPost(request);
}

function* handleLoadPost(request) {
  try {
    if (yield shouldWaitOnAuthDone()) {
      yield take("FIREBASE_AUTH_DONE");
    }
    const result = yield call(api.get, request.url, request.config);
    const post = result.data;
    yield put(loadCurrentPostSuccess(post));
    incrementPostViewsMP(1);
    yield put(loadPostComments(post._id));
    yield put(loadSimilarPosts(post._id));
  } catch (error) {
    yield put(loadCurrentPostFailed(error));
  }
}

function* handleDeletePostsRequests({ postId, redirectionRoute, asAdmin = false }) {
  //Hide Post Modal if open
  yield put(hidePostModal());
  yield put(deletePostOptimistic(postId));
  yield put(showAlert("Deleting case...", "info"));

  let request = asAdmin ? PostApi.getDeletePostAsAdminUrl(postId) : PostApi.getDeletePostUrl(postId);
  try {
    yield call(api.delete, request.url);

    if (redirectionRoute) {
      yield put(push(redirectionRoute));
    }
    yield put(showAlert("Case deleted successfully"));
  } catch (error) {
    yield put(deletePostFailed(postId));
    yield put(showAlert("Unable to delete the case", "error"));
  }
}

function* handleHidePostsRequests({ postId, redirectionRoute, asAdmin = false }) {
  //Hide Post Modal if open
  yield put(hidePostModal());
  yield put(hidePostOptimistic(postId));

  let request = PostApi.getHidePostAsAdminUrl(postId);
  try {
    yield call(api.put, request.url, request.config);

    if (redirectionRoute) {
      yield put(push(redirectionRoute));
    }
    yield put(showAlert("Case is now hidden from the timeline"));
  } catch (error) {
    yield put(hidePostFailed(postId));
    yield put(showAlert("Unable to hide the case", "error"));
  }
}

function* handleUnhidePostsRequests({ postId, refetchCurrentPost, redirectionRoute, asAdmin = false }) {
  const isPostModalOpen = yield select(state => state.posts.isPostModalOpen);
  if (isPostModalOpen) {
    yield put(hidePostModal());
  }

  yield put(unhidePostOptimistic(postId));

  let request = PostApi.getUnhidePostAsAdminUrl(postId);
  try {
    yield call(api.put, request.url, request.config);

    if (redirectionRoute) {
      yield put(push(redirectionRoute));
    }

    if (refetchCurrentPost) {
      yield put(loadCurrentPostByIdRequest(postId));
    }

    yield put(showAlert("Case is now visible in the timeline"));
  } catch (error) {
    yield put(unhidePostFailed(postId));
    yield put(showAlert("Unable to unhide the case", "error"));
  }
}

function* handleFloatPostsRequests({ postId }) {
  const PostModalOpen = yield select(state => state.posts.isPostModalOpen);
  if (PostModalOpen) {
    yield put(hidePostModal());
  }

  let request = PostApi.getFloatPostAsAdminUrl(postId);
  try {
    yield call(api.post, request.url, request.config);

    yield put(showAlert("Case is at the top. reload to see the changes"));
  } catch (error) {
    yield put(unhidePostFailed(postId));
    yield put(showAlert("Unable to move the case", "error"));
  }
}

function* handleLikePostRequests({ postId }) {
  yield put(likePostOptimistic(postId));

  const request = PostApi.getTogglePostLikeUrl(postId);
  try {
    yield call(api.put, request.url);
    yield put(incrementUserLikes());
    windowDataLayer.push({ event: "like" });
  } catch (error) {
    yield put(likePostFailed(postId));
  }
}

function* handleUnlikePostRequests({ postId }) {
  yield put(unlikePostOptimistic(postId));

  const request = PostApi.getTogglePostLikeUrl(postId);
  try {
    yield call(api.put, request.url);
  } catch (error) {
    yield put(unlikePostFailed(postId));
  }
}

function* handleLoadPostCommentsRequests({ postId, limit, pagingToken }) {
  const loadPostCommentsRequest = PostApi.getLoadPostCommentsUrl(postId, {
    limit,
    pagingToken,
  });
  try {
    yield put(setCurrentPostCommentsFetchingStatus(true));
    const { data } = yield call(api.get, loadPostCommentsRequest.url, loadPostCommentsRequest.config);
    const { data: comments, pagingToken } = data;
    yield put(loadPostCommentsSuccess({ comments, pagingToken, limit }));
    yield put(setCurrentPostCommentsFetchingStatus(false));
  } catch (error) {
    yield put({ type: "LOAD_POST_COMMENTS_FAILED" });
  }
}

function* handleApplyPostCategoryFilter({ categoryId }) {
  yield put(setPostCategoryFilter(categoryId));
  yield put(clearPosts());
}

function* handleLoadSimilarPostsRequest({ postId, pagingToken }) {
  const loadSimilarPostsRequest = PostApi.getLoadSimilarPostsUrl(postId, {
    pagingToken,
  });
  try {
    const { data } = yield call(api.get, loadSimilarPostsRequest.url, loadSimilarPostsRequest.config);
    const { data: similarPosts, pagingToken } = data;
    yield put(loadSimilarPostsSuccess({ similarPosts, pagingToken }));
  } catch (error) {
    console.log(error);
  }
}

function* handleRatePostRequests({ postId, rating }) {
  const ratePostRequest = PostApi.getRatePostRequest(postId, rating);
  try {
    yield call(api.post, ratePostRequest.url, ratePostRequest.config);
    yield put(ratePostSuccess(postId));
    mixpanel.track("post.rate", { postId, rating });
  } catch (error) {
    yield put(showAlert("Couldn't rate the case", "error"));
    yield put(ratePostFailed(postId));
  }
}

///////////////////////// WATCHERS \\\\\\\\\\\\\\\\\\\\\\\\\
export function* watchFeedRequests() {
  yield takeLatest(`${LOAD_POSTS_ACTION_BASE}_REQUEST`, handleLoadPostsRequests);
  yield takeLatest(`${LOAD_CURRENT_POST_BY_SLUG_ACTION_BASE}_REQUEST`, handleLoadPostBySlugRequests);
  yield takeLatest(`${LOAD_CURRENT_POST_BY_ID_ACTION_BASE}_REQUEST`, handleLoadPostByIdRequests);
  yield takeLatest(`${DELETE_POST_ACTION_BASE}_REQUEST`, handleDeletePostsRequests);
  yield takeLatest(`${HIDE_POST_ACTION_BASE}_REQUEST`, handleHidePostsRequests);
  yield takeLatest(`${UNHIDE_POST_ACTION_BASE}_REQUEST`, handleUnhidePostsRequests);
  yield takeLatest(`${FLOAT_POST_ACTION_BASE}_REQUEST`, handleFloatPostsRequests);
  yield takeLatest(`${LIKE_POST_ACTION_BASE}_REQUEST`, handleLikePostRequests);
  yield takeLatest(`${UNLIKE_POST_ACTION_BASE}_REQUEST`, handleUnlikePostRequests);
  yield takeLatest(`${LOAD_POST_COMMENTS_ACTION_BASE}_REQUEST`, handleLoadPostCommentsRequests);
  yield takeLatest(APPLY_POST_CATEGORY_FILTER, handleApplyPostCategoryFilter);
  yield takeLatest("LOAD_SIMILAR_POSTS_REQUEST", handleLoadSimilarPostsRequest);
  yield takeLatest("RATE_POST_REQUEST", handleRatePostRequests);
}

export default watchFeedRequests;



// WEBPACK FOOTER //
// ./src/sagas/FeedSaga.js